import React, { useState } from 'react';
import { api } from '../services/api';
import { RefreshCw, Copy, CheckCircle, Shield, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import './PasswordGenerator.css';

const PasswordGenerator: React.FC = () => {
  const [length, setLength] = useState(16);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const generatePassword = async () => {
    setGenerating(true);
    setCopied(false);
    try {
      const response = await api.get(`/utils/generate-password?length=${length}`);
      setGeneratedPassword(response.data.password);
      toast.success('Password generated successfully!');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to generate password');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!generatedPassword) return;
    
    try {
      await navigator.clipboard.writeText(generatedPassword);
      setCopied(true);
      toast.success('Password copied to clipboard!');
      setTimeout(() => {
        // Clear clipboard after 30 seconds for security
        navigator.clipboard.writeText('');
      }, 30000);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy password');
    }
  };

  const getPasswordStrength = (password: string): { label: string; color: string; level: number } => {
    if (!password) return { label: '', color: '', level: 0 };

    const length = password.length;
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSymbol = /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(password);

    let score = 0;
    if (length >= 8) score++;
    if (length >= 12) score++;
    if (length >= 16) score++;
    if (hasUpper) score++;
    if (hasLower) score++;
    if (hasNumber) score++;
    if (hasSymbol) score++;

    if (score <= 2) return { label: 'Weak', color: 'var(--color-danger-500)', level: 1 };
    if (score <= 4) return { label: 'Fair', color: 'var(--color-warning-500)', level: 2 };
    if (score <= 6) return { label: 'Good', color: 'var(--color-primary-500)', level: 3 };
    return { label: 'Strong', color: 'var(--color-success-500)', level: 4 };
  };

  const strength = generatedPassword ? getPasswordStrength(generatedPassword) : null;

  return (
    <div className="password-generator">
      <div className="generator-header">
        <div className="header-icon">
          <Shield size={32} />
        </div>
        <div>
          <h1>Password Generator</h1>
          <p>Create strong, secure passwords instantly</p>
        </div>
      </div>

      <div className="generator-card">
        <div className="card-section">
          <h2>Settings</h2>
          <div className="form-group">
            <label htmlFor="length">
              Password Length: <strong>{length}</strong>
            </label>
            <input
              type="range"
              id="length"
              min="8"
              max="128"
              value={length}
              onChange={(e) => setLength(parseInt(e.target.value))}
              className="length-slider"
            />
            <div className="length-hints">
              <span>8</span>
              <span>64</span>
              <span>128</span>
            </div>
          </div>

          <div className="info-box">
            <p>
              <strong>Secure Password Features:</strong>
            </p>
            <ul>
              <li>Uppercase letters (A-Z)</li>
              <li>Lowercase letters (a-z)</li>
              <li>Numbers (0-9)</li>
              <li>Special characters (!@#$%^&*...)</li>
              <li>Cryptographically secure random generation</li>
            </ul>
          </div>

          <button
            className="generate-btn"
            onClick={generatePassword}
            disabled={generating}
          >
            <RefreshCw size={20} className={generating ? 'spinning' : ''} />
            <span>{generating ? 'Generating...' : 'Generate Password'}</span>
          </button>
        </div>

        {generatedPassword && (
          <div className="card-section">
            <h2>Generated Password</h2>
            <div className="password-display">
              <div className="password-field">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={generatedPassword}
                  readOnly
                  className="password-input"
                />
                <button
                  className="toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
                <button
                  className="copy-btn"
                  onClick={handleCopy}
                  title="Copy password"
                >
                  {copied ? <CheckCircle size={20} /> : <Copy size={20} />}
                </button>
              </div>
              {strength && (
                <div className="strength-indicator">
                  <div className="strength-bar">
                    {[1, 2, 3, 4].map((seg) => (
                      <span
                        key={seg}
                        className="strength-segment"
                        style={{ backgroundColor: seg <= strength.level ? strength.color : undefined }}
                      />
                    ))}
                  </div>
                  <span className="strength-badge" style={{ color: strength.color }}>
                    {strength.label}
                  </span>
                </div>
              )}
            </div>

            <div className="security-warning">
              <Shield size={16} />
              <p>
                <strong>Security Note:</strong> This password will be automatically cleared from your clipboard after 30 seconds for security purposes.
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="tips-card">
        <h3>Password Best Practices</h3>
        <div className="tips-grid">
          <div className="tip-item">
            <strong>Length Matters</strong>
            <p>Use at least 16 characters for better security. Longer passwords are harder to crack.</p>
          </div>
          <div className="tip-item">
            <strong>Unique Passwords</strong>
            <p>Never reuse passwords across different accounts. Each account should have its own unique password.</p>
          </div>
          <div className="tip-item">
            <strong>Regular Updates</strong>
            <p>Change passwords periodically, especially for sensitive accounts. Consider updating every 90 days.</p>
          </div>
          <div className="tip-item">
            <strong>Password Manager</strong>
            <p>Use a password manager to store and manage your passwords securely. Never write passwords down.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordGenerator;

